import { useRef } from 'react'
import styles from './TopServices.module.scss'
import Button from 'components/common/Button/Button'
import animationStyles from 'components/common/Animation/Animation.module.scss'
import useElementVisible from 'components/common/Animation/useElementVisible'

const TopServices: React.FC = () => {
  // refとisVisibleを配列で管理
  const refs = [
    useRef<HTMLDivElement | null>(null),
    useRef<HTMLDivElement | null>(null),
    useRef<HTMLDivElement | null>(null),
    useRef<HTMLDivElement | null>(null),
    useRef<HTMLDivElement | null>(null),
  ]
  const isVisibleArray = refs.map((ref) => useElementVisible(ref, 0.1))

  const services = [
    {
      titleEn: 'Engineering',
      titleJa: 'モダンアプリケーション開発',
      text: (
        <>
          開発チームが直面する課題を解決し、柔軟で適応力の高い開発体制を構築することでビジネスの成長を支えます。AIを活用したアプリケーション開発、内製化支援、クラウドネイティブな開発基盤の整備を包括的に支援します。
        </>
      ),
      links: [
        { href: '/services/ai-driven-development/', text: 'AI駆動開発支援' },
        { href: '/services/application-development/', text: 'アプリケーション開発' },
        { href: '/services/cloudnative/', text: 'クラウドネイティブ' },
        { href: '/services/modernization/', text: 'モダナイゼーション' },
        { href: '/services/feature-engineering/', text: '特徴量エンジニアリングと生成AI' },
      ],
    },
    {
      titleEn: 'Design',
      titleJa: 'デジタルプロダクトデザイン',
      text: (
        <>
          ユーザー行動データを分析し、デザインシステムを核とした直感的かつ操作性の高いUX/UIを設計します。
          スケーラブルなコンポーネントライブラリと最新フロントエンドスタックを組み合わせ、ブランドトーンを統一したシームレスな体験をあらゆる接点で提供します。
        </>
      ),
      links: [
        { href: '/services/design-system/', text: 'デザインシステム構築支援' },
        { href: '/services/prototyping/', text: 'プロトタイピング・仮説検証' },
        { href: '/services/uxui-improvement/', text: 'UX/UI改善' },
        { href: 'https://www.i3design.jp/services/frontend-dev/', text: 'フロントエンド開発', external: true },
      ],
    },
    {
      titleEn: 'Consulting',
      titleJa: 'プロダクト成長支援',
      text: (
        <>
          ビジネス要求定義からグロース戦略の策定・実行までを一貫支援し、プロダクトやサービスの成長を加速。
          <br />
          PdM/PMM支援や新規事業創出を通じ、0→1の立ち上げから事業拡大まで、あらゆる成長フェーズで企業の競争力強化を支援します。
        </>
      ),
      links: [
        { href: '/services/pdm-pmm/', text: 'PdM/PMM体制実行支援' },
        { href: '/services/business-startup-support/', text: '新規事業プロジェクト実行支援' },
      ],
    },
    {
      titleEn: 'Products',
      titleJa: '製品・サービス',
      products: [
        {
          href: 'https://phennec.ai/',
          text: 'Phennec',
          desc: 'AIエージェントの真価を引き出す「コンテキスト・エンジニアリング」基盤',
          imgSrc: '/img/services/services_phennec.webp',
          external: true,
        },
        {
          href: '/services/flamingo/',
          text: 'flamingo',
          desc: 'ウェブシステムのUX/UIデザインをモダナイズするSaaS',
          imgSrc: '/img/services/services_flamingo.webp',
          external: true,
        },
        {
          href: '/services/zebra/',
          text: 'ZEBRA',
          desc: '最適にセグメント配信可能なCRM型のプッシュ通知エンジン',
          imgSrc: '/img/services/services_zebra.webp',
          external: false,
        },
      ],
    },
  ]

  return (
    <section className={styles.topServices}>
      <div className={styles.inner}>
        <h2
          className={`${styles.topServices__title} ${animationStyles.fade} ${
            isVisibleArray[0] ? animationStyles.fadeOn : ''
          }`}
          ref={refs[0]}
        >
          提供サービス
        </h2>

        {services.map((service, index) => (
          <article
            key={index}
            className={`${styles.topSL} ${animationStyles.fade} ${
              isVisibleArray[index + 1] ? animationStyles.fadeOn : ''
            }`}
            ref={refs[index + 1]}
          >
            <h3 className={styles.topSL__title}>
              <span className={styles.topSL__title__en}>{service.titleEn}</span>
              <span className={styles.topSL__title__ja}>{service.titleJa}</span>
            </h3>
            {service.text && <p className={styles.topSL__text}>{service.text}</p>}
            <ul className={styles.topSL__items}>
              {service.links?.map((link, idx) => (
                <li key={idx} className={styles.topSL__items__item}>
                  <a
                    href={link.href}
                    target={link.external ? '_blank' : ''}
                    className={link.external ? styles.window : ''}
                  >
                    <span>{link.text}</span>
                  </a>
                </li>
              ))}
              {service.products?.map((product, idx) => (
                <li key={idx} className={styles.topSL__items__item}>
                  <a
                    href={product.href}
                    target={product.external ? '_blank' : ''}
                    className={product.external ? styles.window : ''}
                  >
                    <h4 className={styles.topSL__items__item__subT}>
                      <span>{product.text}</span>
                    </h4>
                    <p className={styles.topSL__items__item__text}>{product.desc}</p>
                    <figure className={styles.topSL__items__item__img}>
                      <img
                        src={product.imgSrc}
                        alt={product.text}
                        width={480}
                        height={360}
                        className={styles.topSL__items__item__img__image}
                      />
                    </figure>
                  </a>
                </li>
              ))}
            </ul>
          </article>
        ))}

        <div className={styles.topSL__contact}>
          <p className={styles.topSL__contact__text}>貴社が解決したい課題、達成したいミッションをお聞かせください。</p>
          <Button text="相談してみる" href="/contact/" red="red" />
        </div>
      </div>
    </section>
  )
}

export default TopServices
